/**
 * AccountMenu — the header's account chip: round avatar + handle (email until
 * the profile claim lands) + role badge, opening a dropdown with My page /
 * Account settings / Sign out. Signed-out visitors get the plain Sign-in link.
 * Click-outside and Escape close the dropdown.
 */
import { useEffect, useRef, useState } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { useAuth, useMyProfile, useServices } from '../services/context.js';
import { UserAvatar } from './UserAvatar.js';

export function AccountMenu() {
  const { user } = useAuth();
  const services = useServices();
  const { profile } = useMyProfile();
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  if (!user || user.isAnonymous) {
    return (
      <NavLink to="/login" className="btn btn--sm">
        Sign in
      </NavLink>
    );
  }

  const handle = profile?.handle ?? '';
  const display = handle ? `@${handle}` : (user.email ?? user.displayName ?? user.uid.slice(0, 8));

  return (
    <div className="account-menu" ref={rootRef} data-testid="account-menu">
      <button
        type="button"
        className="account-menu__trigger"
        onClick={() => setOpen((was) => !was)}
        aria-haspopup="menu"
        aria-expanded={open}
        data-testid="account-menu-trigger"
      >
        <UserAvatar uid={user.uid} size={26} />
        <span className="account-menu__name">{display}</span>
        {user.role === 'superadmin' && <span className="role-badge role-badge--admin">admin</span>}
        {user.role === 'creator' && <span className="role-badge">creator</span>}
      </button>
      {open && (
        <div className="account-menu__dropdown" role="menu" data-testid="account-menu-dropdown">
          {handle ? (
            <Link
              to={`/u/${handle}`}
              role="menuitem"
              className="account-menu__item"
              onClick={() => setOpen(false)}
              data-testid="account-menu-mypage"
            >
              My page
            </Link>
          ) : (
            // No handle claimed yet — the page can't exist; keep the slot
            // visible but inert until the /welcome claim lands.
            <span
              className="account-menu__item account-menu__item--disabled"
              aria-disabled="true"
              data-testid="account-menu-mypage"
            >
              My page
            </span>
          )}
          <Link
            to="/account"
            role="menuitem"
            className="account-menu__item"
            onClick={() => setOpen(false)}
            data-testid="account-menu-settings"
          >
            Account settings
          </Link>
          <button
            type="button"
            role="menuitem"
            className="account-menu__item account-menu__item--button"
            onClick={() => {
              setOpen(false);
              void services.auth.signOut();
            }}
            data-testid="account-menu-signout"
          >
            Sign out
          </button>
        </div>
      )}
    </div>
  );
}
